import { Job, SkipsEmailResult, SkipsResult, type SkipsInExe } from "@domain/CRM/job";
import { StandardAxiosConfig, blobErrorDetail, getBaseDataURL, parseDates } from "../../../common";
import { DataAPI_Base } from "../base";
import { apiClient } from "../../../client";

/** The /jobs/issues/* queues behind the Admin pages, and the actions that clear them. Split out of `jobs.ts` for its
 *  length; reached as `JobsAPI.issues`. */
export class JobIssuesAPI extends DataAPI_Base<Job> {
	constructor() {
		super("jobs", Job);
	}

	/** GET one /jobs/issues/<path> list as Job instances; `failure` is the error thrown on a non-200. */
	private async getIssueJobs(path: string, failure: string): Promise<Job[]> {
		const url = `${getBaseDataURL("jobs")}/issues/${path}`;
		const response = await apiClient.get<Job[]>(url, StandardAxiosConfig());
		if (response.status !== 200) throw new Error(failure);
		const jobs = response.data.map((j) => Object.assign(new Job(), j));
		parseDates<Job>(jobs);
		return jobs;
	}

	getAgrian(): Promise<Job[]> {
		return this.getIssueJobs("agrian", "Failed to get Agrian issues");
	}

	async fixAgrian(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/agrian`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Agrian issues`);
		} else {
			return response.data;
		}
	}

	getDataSendback(): Promise<Job[]> {
		return this.getIssueJobs("data-sendback", "Failed to get data sendback issues");
	}

	getDealMatcherJobs(): Promise<Job[]> {
		return this.getIssueJobs("deal-matcher", "Failed to get unmatched deal jobs");
	}

	getBranchMatcherJobs(): Promise<Job[]> {
		return this.getIssueJobs("branch-matcher", "Failed to get unmatched branch jobs");
	}

	async fixDealMatcherJobs(ids: string[]): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/deal-matcher`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix unmatched deal jobs`);
		} else {
			return response.data;
		}
	}

	getExeMissionValidationJobs(): Promise<Job[]> {
		return this.getIssueJobs("exe-mission-validation", "Failed to get Exe Mission Validation jobs");
	}

	getGeoValidationJobs(): Promise<Job[]> {
		return this.getIssueJobs("geo-validation", "Failed to get Geo validation jobs");
	}

	getStatusIssueJobs(): Promise<Job[]> {
		return this.getIssueJobs("status", "Failed to get Status Calculator jobs");
	}

	getManualReviewJobs(): Promise<Job[]> {
		return this.getIssueJobs("manual-review", "Failed to get manual review jobs");
	}

	getMiscIssueJobs(): Promise<Job[]> {
		return this.getIssueJobs("misc", "Failed to get misc job errors");
	}

	getMapmakingJobs(): Promise<Job[]> {
		return this.getIssueJobs("mapmaking", "Failed to get Mapmaking jobs");
	}

	getSkipsJobs(): Promise<Job[]> {
		return this.getIssueJobs("skips", "Failed to get skips");
	}

	/** The map the skips email carries, as PNG bytes — shown in the composer before Send. A failure's
	 *  `detail` arrives as a Blob under `responseType: "blob"`, so it is read back into the Error here,
	 *  where the VM can show "no executed mission" apart from "the map service is down". */
	async getSkipsMap(jobId: string): Promise<Blob> {
		const url = `${getBaseDataURL("jobs")}/issues/skips/${encodeURIComponent(jobId)}/map`;
		try {
			const response = await apiClient.get<Blob>(url, { ...StandardAxiosConfig(), responseType: "blob" });
			return response.data;
		} catch (err: any) {
			const detail = await blobErrorDetail(err);
			throw new Error(detail || err?.message || "Failed to load the skips map");
		}
	}

	/** Emails the customer about the job's skips (from the logged-in user, map inline) and flags the
	 *  job "Skips - Customer Contacted"; the result carries the flags as stored, or null with a
	 *  `flagError` when the mail went out but the flag write did not. A failure's server `detail`
	 *  (a 404 no-mission, a 422 rejected recipient, a 502 provider failure) becomes the Error's
	 *  message, the same translation its sibling `getSkipsMap` makes. */
	async sendSkipsEmail(
		jobId: string,
		email: { to: string[]; bcc: string[]; subject: string; body: string },
	): Promise<SkipsEmailResult> {
		const url = `${getBaseDataURL("jobs")}/issues/skips/${encodeURIComponent(jobId)}/email`;
		try {
			const response = await apiClient.post<SkipsEmailResult>(url, email, StandardAxiosConfig());
			return response.data;
		} catch (err: any) {
			const detail = await blobErrorDetail(err);
			throw new Error(detail || err?.message || "Failed to send the skips email");
		}
	}

	/** Runs the Skip Management flow for one job, via the pre-sampling-processor lambda.
	 *  Synchronous on the server and slow by design (it re-runs the exit interview): tens of
	 *  seconds. `staged` is what the operator changed on the row and is the request body as-is —
	 *  the server embeds each under exactly these names and saves the classification and bill to
	 *  Airtable before the run starts (the lambda branches on the classification), and Marked
	 *  Ready after it. A key left out, or undefined (JSON drops it), leaves the record's value
	 *  alone. */
	async processSkips(
		jobId: string,
		staged: { classification?: SkipsInExe; billablePercent?: number; markedReady?: boolean } = {},
	): Promise<SkipsResult> {
		const url = `${getBaseDataURL("jobs")}/issues/skips/${encodeURIComponent(jobId)}`;
		const response = await apiClient.post<SkipsResult>(url, staged, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to process skips`);
		}
		return response.data;
	}

	/** Approve a job's manual review: the API adds the "Manual Review Approved" flag, then re-runs the exit
	 *  interview and answers once it has finished. Resolves to the job's flags. */
	async approveManualReview(jobId: string): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/issues/manual-review/${encodeURIComponent(jobId)}`;
		const response = await apiClient.post<string[]>(url, undefined, StandardAxiosConfig());
		if (response.status !== 200) throw new Error("Failed to approve the manual review");
		return response.data;
	}

	/** Re-process a job's geo data (the pre-sampling processor's process-geo flow); resolves once it has finished.
	 *  `skipValidation` runs it without the geo-spatial validation, which clears the job's validation errors. */
	async processGeo(jobId: string, { skipValidation = false }: { skipValidation?: boolean } = {}): Promise<void> {
		const skip = skipValidation ? "?skip=true" : "";
		const url = `${getBaseDataURL("jobs")}/issues/geo-validation/${encodeURIComponent(jobId)}${skip}`;
		const response = await apiClient.post(url, undefined, StandardAxiosConfig());
		if (response.status !== 200) throw new Error("Failed to re-process the geo data");
	}

	/** Flags the job "Geo Errors Ignored", which takes it off the Geo Validation queue with its validator errors left
	 *  as they are. Resolves to the job's flags as stored. */
	async ignoreGeoErrors(jobId: string): Promise<string[]> {
		const url = `${getBaseDataURL("jobs")}/issues/geo-validation/${encodeURIComponent(jobId)}/ignore`;
		const response = await apiClient.post<string[]>(url, undefined, StandardAxiosConfig());
		if (response.status !== 200) throw new Error("Failed to ignore the geo errors");
		return response.data;
	}

	async fixExeMissionValidationJobs(ids: string[], force = false): Promise<void> {
		const url = `${getBaseDataURL("jobs")}/issues/exe-mission-validation${force ? "?force=true" : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix Exe Mission Validation jobs`);
		} else {
			return response.data;
		}
	}

	async fixDataSendback(ids: string[], forceEmail = false, forceApi = false): Promise<void> {
		const params = new URLSearchParams();
		if (forceEmail) params.set("force_email", "true");
		if (forceApi) params.set("force_api", "true");
		const qs = params.toString();
		const url = `${getBaseDataURL("jobs")}/issues/data-sendback${qs ? `?${qs}` : ""}`;
		const response = await apiClient.post(url, ids, StandardAxiosConfig());
		if (response.status !== 200) {
			throw new Error(`Failed to fix data sendback issues`);
		}
		return response.data;
	}
}
